import React from 'react';
import { motion } from 'framer-motion';
import {
  FaBullseye,
  FaLayerGroup,
  FaDumbbell,
  FaChartLine,
  FaCheckCircle,
  FaFire,
  FaShieldAlt
} from 'react-icons/fa';

export const trainingStepsData = [
  {
    step: '01',
    title: 'Assess',
    description: 'Understand the client’s goal, experience, movement ability, schedule and current fitness level.',
    icon: <FaBullseye className="w-5 h-5 text-red-500" />
  },
  {
    step: '02',
    title: 'Plan',
    description: 'Select exercises, training frequency, volume, intensity and progression around the individual.',
    icon: <FaLayerGroup className="w-5 h-5 text-red-500" />
  },
  {
    step: '03',
    title: 'Train',
    description: 'Coach technique, execution, effort and consistency.',
    icon: <FaDumbbell className="w-5 h-5 text-red-500" />
  },
  {
    step: '04',
    title: 'Track',
    description: 'Monitor training performance, measurements and relevant progress markers.',
    icon: <FaChartLine className="w-5 h-5 text-red-500" />
  },
  {
    step: '05',
    title: 'Adapt',
    description: 'Modify the plan according to progress, recovery and changing goals.',
    icon: <FaCheckCircle className="w-5 h-5 text-red-500" />
  },
  {
    step: '06',
    title: 'Progress',
    description: 'Gradually increase training demands while maintaining quality and sustainability.',
    icon: <FaFire className="w-5 h-5 text-red-500" />
  }
];

export const keyPrinciplesData = [
  'Science-backed programming',
  'Progressive overload',
  'Correct technique',
  'Mobility',
  'Recovery',
  'Measurable progress'
];

const TrainingApproach = () => {
  const transition = { type: 'spring', duration: 2.5 };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { ...transition, delay: i * 0.08 },
    }),
  };

  return (
    <section className="relative px-4 md:px-12 py-16 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-12" id="training-approach">
      {/* Background Glows */}
      <div className="absolute w-80 h-80 bg-red-600/15 blur-[160px] rounded-full top-0 left-1/3 pointer-events-none -z-10" />
      <div className="absolute w-80 h-80 bg-red-800/10 blur-[170px] rounded-full bottom-0 right-1/3 pointer-events-none -z-10" />

      {/* Header */}
      <motion.div
        className="text-center max-w-3xl mx-auto flex flex-col items-center gap-3"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <span className="inline-flex items-center gap-2 bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg">
          <FaFire /> 6-Step Methodical Framework
        </span>

        <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-wide">
          TRAINING <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-red-600">APPROACH</span>
        </h2>

        <p className="text-gray-300 text-xs sm:text-sm max-w-2xl leading-relaxed">
          Our systematic coaching approach ensures every member receives individualized programming, technique correction, and measurable progress tracking.
        </p>
      </motion.div>

      {/* 6 Steps Grid Flow */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {trainingStepsData.map((item, idx) => (
          <motion.div
            key={item.step}
            custom={idx}
            initial="hidden"
            whileInView="visible"
            variants={fadeUp}
            viewport={{ once: true, margin: '-40px' }}
            whileHover={{ y: -8, scale: 1.015 }}
            className="group relative bg-gradient-to-b from-[#191b26]/95 via-[#13151f]/95 to-[#0d0e14]/95 border border-white/10 hover:border-red-500/60 p-6 rounded-3xl transition-all duration-300 shadow-xl overflow-hidden flex flex-col justify-between"
          >
            {/* Top Glowing Strip */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent group-hover:via-red-400 transition-all" />

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-600/25 to-red-900/25 border border-red-500/30 flex items-center justify-center text-red-500 group-hover:bg-red-600 group-hover:text-white transition-all shadow-md">
                  {item.icon}
                </div>
                <span className="text-2xl font-black text-white/20 group-hover:text-red-500/40 transition-colors">
                  {item.step}
                </span>
              </div>

              <h3 className="text-lg font-black uppercase text-white group-hover:text-red-400 transition-colors">
                {item.title}
              </h3>

              <p className="text-xs text-gray-300 leading-relaxed">
                {item.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Key Principles Banner */}
      <motion.div
        className="bg-gradient-to-r from-red-600/20 via-[#181a24] to-red-600/20 p-6 md:p-8 rounded-3xl border border-red-500/30 backdrop-blur-xl flex flex-col gap-4 shadow-2xl"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-white/10 pb-4">
          <span className="text-xs font-black uppercase tracking-widest text-red-400 flex items-center gap-2">
            <FaShieldAlt className="text-red-500" /> Core Principles
          </span>
          <span className="text-sm font-black text-white uppercase tracking-wider">
            Foundational Guidelines for Every Client
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {keyPrinciplesData.map((principle, pIdx) => (
            <div
              key={pIdx}
              className="bg-white/5 border border-white/10 px-3.5 py-2.5 rounded-xl text-center text-xs font-bold text-gray-200 hover:text-white hover:border-red-500/40 transition-all flex items-center justify-center gap-1.5 shadow-sm"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              <span>{principle}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default TrainingApproach;
